import React, { useState } from "react";
import { Button, Divider, Typography, message } from "antd";
import { useMutation } from "@apollo/client";
import { isEmpty, isFunction } from "lodash";
import { DATE_TIME_FORMATS, formatDateTime } from "../../../../utils/dateTimes";
import CustomButton from "../../../commonComponents/button";
import CustomInput from "../../../commonComponents/Input";
import { MESSAGE_NOTES_REPLY } from "../../../../graphql/mutation";
import useLang from "../../../../hooks/useLang";

const { Text, Paragraph } = Typography;

const ReplayNotes = ({ onSend, sending = false, preferences }) => {
  const [value, setValue] = useState("");
  const [lang] = useLang();

  const handleSend = async () => {
    const trimmed = (value || "").trim();
    if (!trimmed) {
      message.warning("Cannot send empty reply");
      return;
    }
    const hasAnyForwardOption =
      preferences && Object.values(preferences).some(value => value === true);

    if (!hasAnyForwardOption) {
      message.info(lang.notesPreferenceInfo);
      return;
    }
    if (isFunction(onSend)) await onSend(trimmed);
    setValue("");
  };

  return (
    // here add input and send button
    <div className="flex items-center gap-2 mt-3">
      <CustomInput
        value={value}
        handleChange={e => setValue(e.target.value)}
        className="!flex-1"
        placeholder="Write reply to this message"
      />
      <Button type="primary" onClick={handleSend} disabled={!value.trim()} loading={sending}>
        Send
      </Button>
    </div>
  );
};

const MessageNotesCard = ({ note = {}, refetchNotes, preferences }) => {
  const { associatedUsers = [], content, childNotes } = note || {};

  const [isReplyClicked, setIsReplyClicked] = useState(false);

  const [replyMutation, { loading: replying }] = useMutation(MESSAGE_NOTES_REPLY);

  // `associatedUsers` is expected to be an array of objects { id, fullName }
  const toText = (Array.isArray(associatedUsers) ? associatedUsers : [])
    .map(u => u?.fullName || "")
    .filter(Boolean)
    .join(", ");

  // Local helper function (keeps logic outside JSX)
  const sendMessageNoteReply = async sentValue => {
    try {
      const messageNoteId = note?.noteId;

      if (!messageNoteId) {
        return;
      }

      const variables = {
        messageNoteId: +messageNoteId,
        response: sentValue,
        messageId: +content?.messageId,
        messageForwardOptions: preferences,
      };

      const resp = await replyMutation({ variables });
      const result = resp?.data?.messageNotesReply;

      if (result && (result.statusCode === 200 || result.statusCode === 201)) {
        message.success(result.message);
        if (isFunction(refetchNotes)) {
          try {
            await refetchNotes();
          } catch (e) {
            console.error("refetchNotes error:", e);
          }
        }

        // close reply UI from inside helper as requested
        setIsReplyClicked(false);
      } else {
        message.error(result?.message);
      }
    } catch (err) {
      message.error(err?.message || "Failed to send reply");
    }
  };

  return (
    <div className="rounded-lg bg-white border border-solid border-[#D9D9D9] p-4">
      <div className="flex flex-col gap-1">
        <Text className="text-black/[0.65] text-xs font-normal">
          <span className="font-normal">To:</span>&nbsp;
          <span className="font-normal">{toText}</span>
        </Text>
        <Text className="text-black/[0.65] text-xs font-normal">
          By: {content?.createdBy?.fullName} •{" "}
          {formatDateTime(content?.createTs, DATE_TIME_FORMATS.MMM_DD_YYYY_COMMA_hh_mm_A)}
        </Text>
      </div>

      <Divider className="!mb-2 !mt-3" style={{ borderBlockStart: "1px solid #d9d9d9" }} />

      <Paragraph
        style={{ whiteSpace: "pre-wrap" }}
        className="!mb-3 !text-black/[0.88] !text-sm !font-normal"
      >
        {content?.notes}
      </Paragraph>

      {!isReplyClicked && (
        <CustomButton
          btnText="Reply"
          className="!p-0"
          type="link"
          handleClick={() => setIsReplyClicked(true)}
        />
      )}

      {isReplyClicked && (
        <ReplayNotes
          sending={replying}
          onSend={async sentValue => {
            await sendMessageNoteReply(sentValue);
          }}
          preferences={preferences}
        />
      )}

      {!isEmpty(childNotes) &&
        childNotes?.map(reply => (
          <div className="bg-[#F5F5F5] flex flex-col gap-3 rounded-lg mt-3 p-3" key={reply?.noteId}>
            <Text className="text-black/[0.55] text-xs font-normal">
              {reply?.content?.createdBy?.fullName} •{" "}
              {formatDateTime(
                reply?.content?.createTs,
                DATE_TIME_FORMATS.MMM_DD_YYYY_COMMA_hh_mm_A
              )}
            </Text>
            <Text className="text-black/[0.88] text-sm font-normal">{reply?.content?.notes}</Text>
          </div>
        ))}
    </div>
  );
};

export default MessageNotesCard;
